/**
 * SecurityCenter — Owner-only dashboard
 *
 * Polls /api/admin/security-center/overview every 60s and renders four
 * sections: AUTHENTICATION · SECURITY HEALTH · THREAT ACTIVITY ·
 * MEMBERSHIP RECONCILIATION + a QUICK ACTIONS strip with typed-confirm
 * guards for destructive operations.
 *
 * Read-only by default. Destructive actions require:
 *   1. Owner role (server enforces 403 otherwise)
 *   2. Typing the exact confirm phrase
 *   3. Click "Execute" (button disabled until phrase matches)
 *
 * Secrets are never rendered — we only show boolean "configured"
 * indicators (✓/✗) for ADMIN_TOKEN, OAuth provider IDs, RESEND_API_KEY.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { FounderStepUp, StepUpBanner } from "./FounderStepUp";
import { stepupStatus, adminRbacMe } from "../../lib/api";
import {
  ShieldCheck,
  ShieldAlert,
  ShieldOff,
  Activity,
  KeyRound,
  Mail,
  Database,
  Users,
  Lock,
  Globe2,
  Cpu,
  AlertTriangle,
  Loader2,
  Download,
  LogOut,
  RotateCw,
  CheckCircle2,
  XCircle,
  Clock,
  Wifi,
  FileText,
} from "lucide-react";
import {
  securityCenterOverview,
  securityCenterForceLogoutAll,
  securityCenterRotateSessions,
  securityCenterExportCsvUrl,
} from "../../lib/api";

const POLL_MS = 60_000;

const fmtRel = (iso) => {
  if (!iso) return "—";
  try {
    const t = new Date(iso).getTime();
    const sec = Math.max(0, Math.round((Date.now() - t) / 1000));
    if (sec < 60) return `${sec}s ago`;
    if (sec < 3600) return `${Math.round(sec / 60)}m ago`;
    if (sec < 86400) return `${Math.round(sec / 3600)}h ago`;
    return `${Math.round(sec / 86400)}d ago`;
  } catch {
    return iso;
  }
};

// ---------- shared cell primitives ----------
const Card = ({ title, icon: Icon, children, color = "#D92D20", testid }) => (
  <section
    className="border border-[#2D2A26] bg-[#0d0c0a] p-4 flex flex-col gap-2"
    data-testid={testid}
  >
    <div className="flex items-center gap-2 mb-1">
      {Icon && <Icon size={13} style={{ color }} />}
      <h3
        className="font-mono text-[10px] uppercase tracking-[0.3em]"
        style={{ color }}
      >
        {title}
      </h3>
    </div>
    {children}
  </section>
);

const Pill = ({ ok, label, value, hint }) => (
  <div className="flex items-center justify-between gap-3 py-1.5 border-b border-[#1f1c18] last:border-b-0">
    <div className="flex items-center gap-2 min-w-0">
      {ok ? (
        <CheckCircle2 size={12} className="text-[#3FB950] shrink-0" />
      ) : (
        <XCircle size={12} className="text-[#D92D20] shrink-0" />
      )}
      <span className="text-[12px] text-[#E4DFD3] font-mono truncate">{label}</span>
    </div>
    <span className="text-[11px] text-[#A39E93] font-mono truncate">
      {value ?? (ok ? "configured" : "missing")}
      {hint && <span className="ml-1 text-[10px] text-[#757065]">· {hint}</span>}
    </span>
  </div>
);

const EmptyRow = () => (
  <div className="text-[11px] text-[#757065] font-mono py-2">No events recorded.</div>
);

const Tag = ({ children, tone = "neutral" }) => {
  const tones = {
    neutral: "border-[#2D2A26] text-[#A39E93]",
    danger:  "border-[#D92D20] text-[#D92D20]",
    warn:    "border-[#E0A526] text-[#E0A526]",
    ok:      "border-[#3FB950] text-[#3FB950]",
    info:    "border-[#3B82F6] text-[#3B82F6]",
  };
  return (
    <span className={`inline-block px-1.5 py-0.5 border text-[9px] uppercase tracking-[0.18em] font-mono ${tones[tone]}`}>
      {children}
    </span>
  );
};

// ---------- typed-confirm modal ----------
const ConfirmModal = ({ open, action, phrase, color, busy, onClose, onExecute }) => {
  const [typed, setTyped] = useState("");
  useEffect(() => { if (!open) setTyped(""); }, [open]);
  if (!open) return null;
  const matches = typed.trim().toUpperCase() === phrase;
  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/75 backdrop-blur-sm"
      role="alertdialog"
      data-testid="sec-confirm-modal"
    >
      <div className="w-[min(480px,92vw)] border-2 bg-[#0a0907] p-6 shadow-2xl" style={{ borderColor: color }}>
        <div className="flex items-center gap-2 mb-2">
          <ShieldAlert size={16} style={{ color }} />
          <h3 className="font-mono text-[11px] uppercase tracking-[0.3em]" style={{ color }}>
            {action}
          </h3>
        </div>
        <p className="text-[13px] text-[#E8E1D2] leading-relaxed mb-3">
          This is a destructive operation. To proceed, type the phrase below exactly.
        </p>
        <div className="border border-[#2D2A26] bg-[#1a0808] px-3 py-2 mb-3">
          <code className="font-mono text-[12px]" style={{ color }}>{phrase}</code>
        </div>
        <input
          type="text"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          placeholder="Type the phrase above…"
          spellCheck={false}
          autoFocus
          data-testid="sec-confirm-input"
          className="w-full px-3 py-2 bg-[#0a0907] border border-[#2D2A26] text-[#E4DFD3] font-mono text-[12px] uppercase tracking-[0.1em] focus:outline-none focus:border-[#D92D20] mb-4"
        />
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            data-testid="sec-confirm-cancel"
            className="px-4 py-2 border border-[#2D2A26] text-[#A39E93] font-mono text-[11px] uppercase tracking-[0.25em] hover:border-[#A39E93] hover:text-[#E4DFD3]"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onExecute}
            disabled={!matches || busy}
            data-testid="sec-confirm-execute"
            className="px-4 py-2 border font-mono text-[11px] uppercase tracking-[0.25em] disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ borderColor: color, color, background: matches ? "#1a0808" : "transparent" }}
          >
            {busy ? <Loader2 size={12} className="animate-spin inline" /> : "Execute"}
          </button>
        </div>
      </div>
    </div>
  );
};

// =========================================================================
// MAIN
// =========================================================================
export const SecurityCenter = ({ opToken, userToken, identity }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [confirmFor, setConfirmFor] = useState(null); // 'force' | 'rotate' | null
  const [busy, setBusy] = useState(false);

  // Ownership is resolved by the SAME source of truth as the RBAC page and
  // the operations shell: GET /admin/rbac/me → rbac.is_owner_user (backend).
  // The `identity` prop from /admin/me carries no owner flag and must never
  // gate access (it silently rejected the real Owner). Operator token is
  // Owner-equivalent break-glass.
  const [isOwner, setIsOwner] = useState(
    identity?.source === "token" ? true : null, // null = resolving
  );
  useEffect(() => {
    if (identity?.source === "token") { setIsOwner(true); return undefined; }
    let cancelled = false;
    adminRbacMe(opToken || null, userToken || null)
      .then((me) => { if (!cancelled) setIsOwner(!!me?.is_owner); })
      .catch(() => { if (!cancelled) setIsOwner(false); });
    return () => { cancelled = true; };
  }, [opToken, userToken, identity?.source]);

  // Operation Lockdown — Founder step-up state
  const [stepup, setStepup] = useState(null);
  const loadStepup = useCallback(async () => {
    try { setStepup(await stepupStatus(opToken || null, userToken || null)); }
    catch { setStepup(false); }
  }, [opToken, userToken]);
  useEffect(() => { if (isOwner) loadStepup(); }, [isOwner, loadStepup]);
  const stepupLocked = !!(stepup && stepup.enrolled && !stepup.elevated);

  const load = useCallback(async () => {
    try {
      const r = await securityCenterOverview(opToken || null, userToken || null);
      setData(r);
      setError(null);
    } catch (e) {
      const code = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (detail === "STEP_UP_REQUIRED") {
        // Elevation lapsed mid-session — surface the step-up prompt, never
        // a misleading "Owner-only" message.
        loadStepup();
        return;
      }
      setError(code === 403 || code === 401
        ? "Owner-only — your account does not have access to the Security Center."
        : (e?.response?.data?.detail || e.message || "Failed to load"));
    } finally {
      setLoading(false);
    }
  }, [opToken, userToken, loadStepup]);

  useEffect(() => {
    if (!isOwner || stepupLocked || stepup === null) { setLoading(stepup === null && isOwner); return undefined; }
    load();
    const id = setInterval(load, POLL_MS);
    return () => clearInterval(id);
  }, [isOwner, load, stepupLocked, stepup]);

  const onForceLogout = useCallback(async () => {
    setBusy(true);
    try {
      const r = await securityCenterForceLogoutAll(opToken, userToken, "LOG OUT ALL ADMINS");
      toast.success(`Force-logout complete · ${r.deleted_sessions} sessions cleared. You will be signed out.`);
      setConfirmFor(null);
      // The Owner just deleted their own session too — best to do a soft reload
      setTimeout(() => { window.location.href = "/admin/login?reason=force_logout"; }, 1200);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Force-logout failed");
    } finally {
      setBusy(false);
    }
  }, [opToken, userToken]);

  const onRotateSessions = useCallback(async () => {
    setBusy(true);
    try {
      const r = await securityCenterRotateSessions(opToken, userToken, "ROTATE ALL SESSIONS");
      toast.success(`Rotated · ${r.rotated} sessions will expire at ${new Date(r.expires_at).toLocaleTimeString()}.`);
      setConfirmFor(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rotate failed");
    } finally {
      setBusy(false);
    }
  }, [opToken, userToken, load]);

  const exportHref = useMemo(() => securityCenterExportCsvUrl(opToken, 90), [opToken]);

  if (isOwner === null) {
    return (
      <div className="flex items-center gap-2 text-[#A39E93] p-8" data-testid="sec-owner-resolving">
        <Loader2 size={14} className="animate-spin" /> Verifying clearance…
      </div>
    );
  }

  if (!isOwner) {
    return (
      <div className="border border-[#2D2A26] bg-[#0d0c0a] p-8 max-w-2xl" data-testid="sec-owner-gate">
        <div className="flex items-center gap-2 mb-3 text-[#D92D20]">
          <ShieldOff size={16} />
          <h2 className="font-mono text-[11px] uppercase tracking-[0.3em]">Owner-Only Access</h2>
        </div>
        <p className="text-[13px] text-[#E4DFD3] leading-relaxed">
          The Security Center is restricted to the Owner role. Your current account
          ({identity?.email || "unknown"}) does not have access.
        </p>
      </div>
    );
  }

  if (stepup === null) {
    return (
      <div className="flex items-center gap-2 text-[#A39E93] font-mono text-[12px]" data-testid="sec-loading">
        <Loader2 size={14} className="animate-spin" /> Checking Founder verification…
      </div>
    );
  }

  // Level 4 gate — Security Center opens ONLY behind a fresh step-up.
  if (stepupLocked) {
    return (
      <div className="max-w-lg" data-testid="sec-stepup-gate">
        <FounderStepUp opToken={opToken} userToken={userToken}
                       onElevated={() => { loadStepup(); setLoading(true); load(); }} />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-[#A39E93] font-mono text-[12px]" data-testid="sec-loading">
        <Loader2 size={14} className="animate-spin" /> Loading Security Center…
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="border border-[#D92D20] bg-[#1a0808] p-6" data-testid="sec-error">
        <div className="flex items-center gap-2 text-[#D92D20] mb-2">
          <AlertTriangle size={14} />
          <span className="font-mono text-[11px] uppercase tracking-[0.3em]">Security Center Unavailable</span>
        </div>
        <p className="text-[13px] text-[#E4DFD3] font-mono">{error}</p>
      </div>
    );
  }

  const a = data.authentication;
  const h = data.security_health;
  const t = data.threat;
  const m = data.membership_reconciliation;

  return (
    <div className="space-y-6" data-testid="security-center">
      <StepUpBanner status={stepup}
                    onVerifyAgain={() => setStepup((s) => ({ ...s, elevated: false }))} />
      {stepup && !stepup.enrolled && (
        <FounderStepUp opToken={opToken} userToken={userToken} onElevated={loadStepup} />
      )}
      {/* Header strip */}
      <div className="border border-[#D92D20] bg-[#1a0808] px-4 py-3 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <ShieldCheck size={14} className="text-[#D92D20]" />
          <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#D92D20]">
            Security Center · Owner Eyes Only
          </span>
        </div>
        <div className="flex items-center gap-2 text-[10px] font-mono text-[#A39E93]">
          <Wifi size={11} /> auto-refresh 60s · last sync {fmtRel(data.generated_at)}
        </div>
      </div>

      {/* ================== AUTHENTICATION ================== */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
        <Card title="Last 10 Successful Admin Logins" icon={KeyRound} testid="sec-card-logins">
          {a.last_admin_logins.length === 0 ? <EmptyRow /> : (
            <ul className="text-[11px] font-mono space-y-1">
              {a.last_admin_logins.map((r, i) => (
                <li key={i} className="flex items-center justify-between gap-2 border-b border-[#1f1c18] last:border-b-0 py-1">
                  <span className="text-[#E4DFD3] truncate">{r.actor_email || "—"}</span>
                  <span className="text-[#757065] shrink-0">{fmtRel(r.ts)} · {r.ip || "—"}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card title="Last 10 Failed Admin Logins" icon={AlertTriangle} testid="sec-card-failed">
          {a.last_failed_logins.length === 0 ? <EmptyRow /> : (
            <ul className="text-[11px] font-mono space-y-1">
              {a.last_failed_logins.map((r, i) => (
                <li key={i} className="flex items-center justify-between gap-2 border-b border-[#1f1c18] last:border-b-0 py-1">
                  <span className="text-[#E4DFD3] truncate">{r.ip_address || "—"} {r.country && <Tag tone="warn">{r.country}</Tag>}</span>
                  <span className="text-[#757065] shrink-0">{fmtRel(r.timestamp)} · {r.failure_reason || "—"}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card title="Last 10 Idle-Lock Events" icon={Clock} testid="sec-card-idle">
          {a.last_idle_locks.length === 0 ? <EmptyRow /> : (
            <ul className="text-[11px] font-mono space-y-1">
              {a.last_idle_locks.map((r, i) => (
                <li key={i} className="flex items-center justify-between gap-2 border-b border-[#1f1c18] last:border-b-0 py-1">
                  <span className="text-[#E4DFD3] truncate">{r.admin_email || "—"} <Tag>{r.timeout_minutes}m</Tag></span>
                  <span className="text-[#757065] shrink-0 truncate max-w-[160px]" title={r.path}>{fmtRel(r.ts)}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card title="Last 10 RBAC Denials" icon={ShieldOff} testid="sec-card-rbac">
          {a.last_rbac_denials.length === 0 ? <EmptyRow /> : (
            <ul className="text-[11px] font-mono space-y-1">
              {a.last_rbac_denials.map((r, i) => (
                <li key={i} className="flex items-center justify-between gap-2 border-b border-[#1f1c18] last:border-b-0 py-1">
                  <span className="text-[#E4DFD3] truncate">{r.actor_email || "—"} <Tag tone="danger">{r.permission || "?"}</Tag></span>
                  <span className="text-[#757065] shrink-0">{fmtRel(r.ts)}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card title={`Active Admin Sessions (${a.active_sessions.total_active})`} icon={Users} testid="sec-card-sessions">
          {a.active_sessions.sample.length === 0 ? <EmptyRow /> : (
            <ul className="text-[11px] font-mono space-y-1">
              {a.active_sessions.sample.map((r, i) => (
                <li key={i} className="flex items-center justify-between gap-2 border-b border-[#1f1c18] last:border-b-0 py-1">
                  <span className="text-[#E4DFD3] truncate">{r.email_masked || r.user_id} {r.role && <Tag tone="info">{r.role}</Tag>}</span>
                  <span className="text-[#757065] shrink-0">{fmtRel(r.last_seen_at || r.created_at)}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card title="Quick Actions" icon={Activity} color="#E0A526" testid="sec-card-actions">
          <div className="space-y-2">
            <Link
              to="/admin/audit"
              data-testid="sec-action-audit"
              className="block w-full text-left px-3 py-2 border border-[#2D2A26] text-[#A39E93] font-mono text-[11px] uppercase tracking-[0.2em] hover:border-[#E0A526] hover:text-[#E0A526]"
            >
              <FileText size={12} className="inline mr-2" /> View full audit log →
            </Link>
            <button
              type="button"
              onClick={() => setConfirmFor("rotate")}
              data-testid="sec-action-rotate"
              className="block w-full text-left px-3 py-2 border border-[#E0A526] text-[#E0A526] font-mono text-[11px] uppercase tracking-[0.2em] hover:bg-[#1a1308]"
            >
              <RotateCw size={12} className="inline mr-2" /> Rotate admin sessions
            </button>
            <button
              type="button"
              onClick={() => setConfirmFor("force")}
              data-testid="sec-action-force"
              className="block w-full text-left px-3 py-2 border border-[#D92D20] text-[#D92D20] font-mono text-[11px] uppercase tracking-[0.2em] hover:bg-[#1a0808]"
            >
              <LogOut size={12} className="inline mr-2" /> Force-logout all admins
            </button>
            <a
              href={exportHref}
              data-testid="sec-action-export"
              className="block w-full text-left px-3 py-2 border border-[#2D2A26] text-[#A39E93] font-mono text-[11px] uppercase tracking-[0.2em] hover:border-[#3FB950] hover:text-[#3FB950]"
            >
              <Download size={12} className="inline mr-2" /> Export security audit (CSV · 90d)
            </a>
          </div>
        </Card>
      </div>

      {/* ================== SECURITY HEALTH ================== */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
        <Card title="Configuration" icon={Cpu} color="#3FB950" testid="sec-card-config">
          <Pill ok={h.admin_token_configured} label="ADMIN_TOKEN" value={h.admin_token_configured ? "set" : "missing"} hint="value never displayed" />
          <Pill ok={h.database.ok} label="Database" value={h.database.ok ? `up · ${h.database.collections_count} collections` : "DOWN"} />
          <Pill ok={!!h.resend.configured} label="Resend (email)" value={h.resend.configured ? (h.resend.from || "configured") : "missing API key"} />
          <Pill ok={h.mfa.totp_seeds_count > 0} label="MFA / TOTP" value={`${h.mfa.totp_seeds_count}/${h.mfa.admin_total} admins`} />
          <Pill ok={!!h.last_backup_at} label="Last backup" value={h.last_backup_at ? fmtRel(h.last_backup_at) : "not wired"} />
          <Pill ok={!!h.last_deployment_at} label="Last deployment" value={fmtRel(h.last_deployment_at)} />
        </Card>

        <Card title="OAuth Providers Configured" icon={Globe2} color="#3FB950" testid="sec-card-oauth">
          <Pill ok={h.oauth_providers.google} label="Google" />
          <Pill ok={h.oauth_providers.microsoft} label="Microsoft" />
          <Pill ok={h.oauth_providers.github} label="GitHub" />
          <Pill ok={h.oauth_providers.apple} label="Apple" />
        </Card>

        <Card title="Last Email Send" icon={Mail} color="#3FB950" testid="sec-card-mail">
          <div className="text-[12px] font-mono text-[#E4DFD3]">
            Status: <span className="text-[#A39E93]">{h.resend.last_send_status || "—"}</span>
          </div>
          <div className="text-[11px] font-mono text-[#757065] mt-1">
            {h.resend.last_send_at ? fmtRel(h.resend.last_send_at) : "no record"}
          </div>
        </Card>
      </div>

      {/* ================== THREAT ACTIVITY ================== */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card title={`Locked IPs (${t.locked_ips.length})`} icon={Lock} testid="sec-card-locked">
          {t.locked_ips.length === 0 ? <EmptyRow /> : (
            <ul className="text-[11px] font-mono space-y-1">
              {t.locked_ips.slice(0, 8).map((r, i) => (
                <li key={i} className="flex items-center justify-between gap-2 border-b border-[#1f1c18] last:border-b-0 py-1">
                  <span className="text-[#E4DFD3] truncate">
                    {r.ip_address || "—"} {r.country && <Tag tone="warn">{r.country}</Tag>}
                  </span>
                  <span className="text-[#757065] shrink-0">{r.total_attempts || 0} attempts · {fmtRel(r.locked_until)}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card title="Rate-Limit Events (last hour)" icon={Activity} testid="sec-card-rate">
          {t.rate_limit_events.length === 0 ? <EmptyRow /> : (
            <ul className="text-[11px] font-mono space-y-1">
              {t.rate_limit_events.map((r, i) => (
                <li key={i} className="flex items-center justify-between gap-2 border-b border-[#1f1c18] last:border-b-0 py-1">
                  <span className="text-[#E4DFD3] truncate">{r.ip || "—"}</span>
                  <span className="text-[#757065] shrink-0">{r.count} hits · {fmtRel(r.last_at)}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card title="Suspicious Login Attempts (24h)" icon={ShieldAlert} testid="sec-card-sus">
          {t.suspicious_logins.length === 0 ? <EmptyRow /> : (
            <ul className="text-[11px] font-mono space-y-1">
              {t.suspicious_logins.map((r, i) => (
                <li key={i} className="flex items-center justify-between gap-2 border-b border-[#1f1c18] last:border-b-0 py-1">
                  <span className="text-[#E4DFD3] truncate">{r.ip || "—"} {r.country && <Tag tone="warn">{r.country}</Tag>}</span>
                  <span className="text-[#757065] shrink-0">{r.count} attempts</span>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card title="New Devices / Countries" icon={Globe2} color="#E0A526" testid="sec-card-newdev">
          <Pill ok={false} label="New device logins" value={t.new_device_logins?.status || "pending"} hint={t.new_device_logins?.note} />
          <Pill ok={false} label="New country logins" value={t.new_country_logins?.status || "pending"} hint={t.new_country_logins?.note} />
        </Card>
      </div>

      {/* ================== MEMBERSHIP RECONCILIATION ================== */}
      <div className="border-2 border-[#E0A526] bg-[#1a1308] p-4" data-testid="sec-membership-recon">
        <div className="flex items-center gap-2 mb-3">
          <Database size={14} className="text-[#E0A526]" />
          <h2 className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#E0A526]">
            Membership Reconciliation (Drift Detector)
          </h2>
        </div>
        <p className="text-[11px] font-mono text-[#A39E93] mb-4 leading-relaxed">
          The three counts below answer DIFFERENT questions and CANNOT be compared directly.
          See <code className="text-[#E0A526]">/app/memory/MEMBER_LIFECYCLE_AUDIT.md</code> for
          the full forensic report. The dead-pool warning will disappear once we run the
          cleanup pass.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
          <div className="border border-[#2D2A26] bg-[#0a0907] p-3">
            <div className="font-mono text-[10px] text-[#3FB950] uppercase tracking-[0.2em] mb-1">members (truth)</div>
            <div className="text-[28px] font-mono text-[#E4DFD3]">{m.members.total}</div>
            <div className="text-[10px] font-mono text-[#757065] mt-1 leading-tight">
              {m.members.verified} verified · {m.members.unverified} unverified<br />
              {m.members.archived} archived · {m.members.founders} founders<br />
              {m.members.missing_status_field > 0 && (
                <span className="text-[#E0A526]">⚠ {m.members.missing_status_field} missing status</span>
              )}
            </div>
          </div>
          <div className="border border-[#D92D20] bg-[#0a0907] p-3">
            <div className="font-mono text-[10px] text-[#D92D20] uppercase tracking-[0.2em] mb-1">subscribers (legacy)</div>
            <div className="text-[28px] font-mono text-[#E4DFD3]">{m.subscribers_legacy.total}</div>
            <div className="text-[10px] font-mono text-[#D92D20] mt-1 leading-tight">
              {m.subscribers_legacy.warning}
            </div>
          </div>
          <div className="border border-[#3B82F6] bg-[#0a0907] p-3">
            <div className="font-mono text-[10px] text-[#3B82F6] uppercase tracking-[0.2em] mb-1">users (admin oauth)</div>
            <div className="text-[28px] font-mono text-[#E4DFD3]">{m.users_admin_oauth.total}</div>
            <div className="text-[10px] font-mono text-[#757065] mt-1 leading-tight">
              {Object.entries(m.users_admin_oauth.by_role || {}).map(([k, v]) => (
                <div key={k}>{v} {k}</div>
              ))}
            </div>
          </div>
        </div>
        <div className="text-[10px] font-mono text-[#757065]">
          Active sessions · member: {m.sessions.member_sessions_active} · admin: {m.sessions.user_sessions_active}
        </div>
      </div>

      {/* ================== CONFIRM MODALS ================== */}
      <ConfirmModal
        open={confirmFor === "force"}
        action="Force-Logout All Admin Sessions"
        phrase="LOG OUT ALL ADMINS"
        color="#D92D20"
        busy={busy}
        onClose={() => setConfirmFor(null)}
        onExecute={onForceLogout}
      />
      <ConfirmModal
        open={confirmFor === "rotate"}
        action="Rotate All Admin Sessions"
        phrase="ROTATE ALL SESSIONS"
        color="#E0A526"
        busy={busy}
        onClose={() => setConfirmFor(null)}
        onExecute={onRotateSessions}
      />
    </div>
  );
};

export default SecurityCenter;
